import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { injectIntl, useIntl } from "react-intl";
import { compose } from "redux";
import { useDispatch, useSelector } from "react-redux";
import {
  setupHoneybadger,
  removeHoneybadger,
} from "../../../../js/utils/errorTracking/honeybadger";
import injectMultipleReducers from "../../../utils/reducers/injectMultipleReducers";
import injectSaga from "../../../utils/saga/injectSaga";
import When from "../../../common/When";
import {
  courseReducer,
  liveSessionReducer,
  liveSessionSchedulePageReducer,
  liveSessionsListReducer,
} from "../reducer";
import saga from "../saga";
import * as LiveSessionStates from "../constants/liveSessionStates";
import * as ReducerKeys from "../constants/reducerKeys";
import {
  useCourseActions,
  useLiveSessionActions,
  useLiveSessionSchedulePageActions,
  useLiveSessionDomainActions,
} from "../customHooks";
import LoaderModal from "../../../common/LoaderModal";
import {
  LiveSessionAlert,
  LiveSessionConfirmationModal,
  LiveSessionScheduleContent,
} from "../component";
import { redirectToCourseLives } from "../utils/pageRedirect";

export const LiveSessionScheduleNew = ({ courseId }) => {
  const intl = useIntl();

  const {
    loading: courseLoading,
    errorFetching: courseFetchingError,
    course,
  } = useSelector((state) => state[ReducerKeys.COURSE]);

  const {
    loading: liveSessionLoading,
    errorSaving: errorSavingLiveSession,
    liveSessionCreated,
  } = useSelector((state) => state[ReducerKeys.LIVE_SESSION]);

  const {
    loading: liveSessionsListLoading,
    errorFetching: liveSessionFetchingError,
    liveSessionFetchSuccess,
    liveSessionsList,
  } = useSelector((state) => state[ReducerKeys.LIVE_SESSIONS_LIST]);

  const { loading: liveSessionSchedulePageLoading } = useSelector(
    (state) => state[ReducerKeys.LIVE_SESSION_SCHEDULE_PAGE]
  );

  const dispatch = useDispatch();
  const { getCourse } = useCourseActions();
  const { createLiveSession } = useLiveSessionActions({ dispatch });
  const { getLiveSessionsList } = useLiveSessionDomainActions();
  const { setLiveSessionSchedulePageLoading } = useLiveSessionSchedulePageActions({ dispatch });

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const newLiveSession = useRef({});

  const isLoading = () =>
    courseLoading ||
    liveSessionLoading ||
    liveSessionsListLoading ||
    liveSessionSchedulePageLoading;

  const showLiveSessionAlert = () =>
    courseFetchingError || liveSessionFetchingError || errorSavingLiveSession;

  const openConfirmationModal = () => {
    setConfirmationModalOpen(true);
  };

  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const goToLivesListPage = (showSetupModal = false) => {
    setLiveSessionSchedulePageLoading();
    redirectToCourseLives(courseId, showSetupModal);
  };

  const submitForm = () => {
    if (newLiveSession?.current) {
      createLiveSession(courseId, { data: newLiveSession.current });
    }
  };

  const onSubmitHandler = ({ liveSession }) => {
    newLiveSession.current = liveSession;
    openConfirmationModal();
  };

  const onConfirmModalHandler = () => {
    closeConfirmationModal();
    submitForm();
  };

  useEffect(() => {
    getCourse(courseId);
    getLiveSessionsList({
      courseId,
      state: [LiveSessionStates.SCHEDULED, LiveSessionStates.READY, LiveSessionStates.STREAMING],
    });
    setupHoneybadger();

    return () => {
      removeHoneybadger();
    };
  }, []);

  useEffect(() => {
    if (course?.id && liveSessionFetchSuccess && liveSessionsList?.length) {
      goToLivesListPage();
    }
  }, [course, liveSessionFetchSuccess, liveSessionsList]);

  useEffect(() => {
    if (liveSessionCreated) {
      goToLivesListPage(true);
    }
  }, [liveSessionCreated]);

  return (
    <>
      <LoaderModal isOpen={isLoading()} messageKey="live_sessions.loading_message" />
      <LiveSessionConfirmationModal
        isOpen={isConfirmationModalOpen}
        showWarningIcon
        title={intl.formatMessage(
          { id: "live_sessions.schedule_new.confirmation_title" },
          { number: course.numberOfStudents }
        )}
        body={intl.formatMessage(
          { id: "live_sessions.schedule_new.confirmation_body" },
          { b: (chunks) => <b>{chunks}</b>, number: course.numberOfStudents }
        )}
        cancelMessage={intl.formatMessage({ id: "generic.cancel" })}
        confirmMessage={intl.formatMessage({
          id: "live_sessions.schedule_new.confirmation_button",
        })}
        onConfirm={onConfirmModalHandler}
        onCancel={closeConfirmationModal}
      />
      <When condition={showLiveSessionAlert()}>
        {() => (
          <LiveSessionAlert
            type="error"
            message="generic.messages.error_try_again"
            top={8}
            position="fixed"
          />
        )}
      </When>
      <LiveSessionScheduleContent
        numberOfStudents={course.numberOfStudents}
        loading={isLoading()}
        onCancel={() => goToLivesListPage()}
        onSubmit={onSubmitHandler}
      />
    </>
  );
};

const withReducers = injectMultipleReducers([
  { key: ReducerKeys.COURSE, reducer: courseReducer },
  { key: ReducerKeys.LIVE_SESSION, reducer: liveSessionReducer },
  { key: ReducerKeys.LIVE_SESSIONS_LIST, reducer: liveSessionsListReducer },
  { key: ReducerKeys.LIVE_SESSION_SCHEDULE_PAGE, reducer: liveSessionSchedulePageReducer },
]);

const withSaga = injectSaga({ key: "course[live]", saga });

export default compose(withReducers, withSaga)(injectIntl(LiveSessionScheduleNew));

LiveSessionScheduleNew.propTypes = {
  courseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};
